import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { regionFromSlug } from './data/guidesLoader';
import { DestinationFlow } from './components/DestinationFlow';

// Heavy, route-split components — loaded on demand so lightweight guide/legal
// pages don't have to download the map app (Mapbox GL, Spline 3D globe, turf).
const HomeLanding = lazy(() => import('./components/HomeLanding').then((m) => ({ default: m.HomeLanding })));
const MapView = lazy(() => import('./components/MapView').then((m) => ({ default: m.MapView })));

// Page imports (code-split per route)
const About = lazy(() => import('./pages/About'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const Contact = lazy(() => import('./pages/Contact'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const GuidesIndex = lazy(() => import('./pages/GuidesIndex'));
const GuideDetail = lazy(() => import('./pages/GuideDetail'));
const GuideHub = lazy(() => import('./pages/GuideHub'));

// /guides/:slug is either a region hub (e.g. /guides/europe) or a city guide.
function GuideSlugRoute() {
  const { slug } = useParams();
  return regionFromSlug(slug) ? <GuideHub /> : <GuideDetail />;
}

// Minimal branded fallback shown only while a route chunk loads.
const RouteFallback = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FDFBF7' }}>
    <div style={{ width: 32, height: 32, border: '3px solid #e7e5e4', borderTopColor: '#78716c', borderRadius: '50%', animation: 'cs-spin 0.7s linear infinite' }} />
    <style>{'@keyframes cs-spin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main app content component
function AppContent() {
  const [view, setView] = useState('landing'); // 'landing' or 'map'
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [accommodations, setAccommodations] = useState([]);
  const [tripType, setTripType] = useState('');
  const [travelerType, setTravelerType] = useState('');
  const [conditionalCenter, setConditionalCenter] = useState(null);
  const [searchInfo, setSearchInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  // Guide-search deep-links carry a destination + dates but no trip/traveler yet —
  // we pause on a trip/traveler question step before entering the map.
  const [flowCity, setFlowCity] = useState(null);
  const [flowSearch, setFlowSearch] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resolvedRef = useRef(false);

  useEffect(() => {
    fetchCities();
  }, []);

  // Deep-link resolver: /?cityId=...&checkin=...&checkout=...&adults=...&children=...
  // (used by the guide-page search bar) opens the same map flow as a homepage search.
  useEffect(() => {
    if (resolvedRef.current) return;
    const cityId = searchParams.get('cityId');
    if (!cityId || cities.length === 0) return;
    const city = cities.find((c) => c.id === cityId);
    if (!city) return;
    resolvedRef.current = true;
    const parseDate = (s) => (s ? new Date(`${s}T00:00:00`) : null);
    const agesParam = searchParams.get('childrenAges');
    const searchDetails = {
      checkIn: parseDate(searchParams.get('checkin')),
      checkOut: parseDate(searchParams.get('checkout')),
      adults: parseInt(searchParams.get('adults') || '2', 10),
      children: parseInt(searchParams.get('children') || '0', 10),
      childrenAges: agesParam ? agesParam.split(',').map((n) => parseInt(n, 10)).filter((n) => !isNaN(n)) : [],
      infants: parseInt(searchParams.get('infants') || '0', 10),
      pets: parseInt(searchParams.get('pets') || '0', 10),
    };
    // Guide search gives destination + dates but not trip/traveler — ask those
    // (same questions as the homepage) before entering the map.
    setFlowCity(city);
    setFlowSearch(searchDetails);
    setView('flow');
    setSearchParams({}, { replace: true });
  }, [cities, searchParams]);

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/cities`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
      toast.error('Failed to load cities');
    }
  };

  const handleCitySelect = async (city, selectedTripType, selectedTravelerType, searchDetails) => {
    setLoading(true);
    setSelectedCity(city);
    setTripType(selectedTripType || '');
    setTravelerType(selectedTravelerType || '');
    setSearchInfo(searchDetails || null);

    try {
      // Fetch conditional center based on trip type and traveler type
      let centerData = null;
      if (selectedTripType && selectedTravelerType) {
        try {
          const centerResponse = await axios.get(
            `${API}/cities/${city.id}/center`,
            { params: { trip_type: selectedTripType, traveler_type: selectedTravelerType } }
          );
          centerData = centerResponse.data;
          setConditionalCenter(centerData);
        } catch (centerError) {
          console.warn('Could not fetch conditional center, using default:', centerError);
          setConditionalCenter(null);
        }
      } else {
        setConditionalCenter(null);
      }
      
      // Fetch accommodations - pass the conditional center if available
      let accommodationsUrl = `${API}/cities/${city.id}/accommodations`;
      if (centerData && centerData.centers && centerData.centers.length > 0) {
        // Use the first center's coordinates for generating hotels
        const firstCenter = centerData.centers[0];
        accommodationsUrl += `?center_lat=${firstCenter.lat}&center_lng=${firstCenter.lng}`;
      }
      const response = await axios.get(accommodationsUrl);
      setAccommodations(response.data);
      
      // Smooth transition to map view
      setTimeout(() => {
        setView('map');
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching accommodations:', error);
      toast.error('Failed to load accommodations');
      setLoading(false);
    }
  };

  const handleBack = () => {
    setView('landing');
    setSelectedCity(null);
    setAccommodations([]);
    setTripType('');
    setTravelerType('');
    setConditionalCenter(null);
    setFlowCity(null);
    setFlowSearch(null);
  };

  return (
    <div className="App">
      {view === 'landing' && (
        <HomeLanding
          onCitySelect={handleCitySelect}
          cities={cities}
        />
      )}
      {view === 'flow' && flowCity && (
        <div
          data-testid="guide-flow-view"
          style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px 16px',
            background: 'radial-gradient(1200px 600px at 70% -10%, #1e293b 0%, #0c0a09 55%, #000 100%)'
          }}
        >
          <DestinationFlow
            city={flowCity}
            onComplete={(tripId, travelerId) => handleCitySelect(flowCity, tripId, travelerId, flowSearch)}
            onClose={() => navigate(-1)}
          />
        </div>
      )}
      {view === 'map' && selectedCity && (
        <MapView 
          city={selectedCity}
          accommodations={accommodations}
          onBack={handleBack}
          tripType={tripType}
          travelerType={travelerType}
          conditionalCenter={conditionalCenter}
          searchInfo={searchInfo}
        />
      )}
    </div>
  );
}

// Resets scroll to the top on every route (pathname) change — laptop & mobile.
// The real scroll container is #root (its `overflow-x:hidden` forces `overflow-y`
// to compute to `auto` while height is 100%), so window/body resets alone miss it.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const toTop = () => {
      window.scrollTo(0, 0);
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const root = document.getElementById('root');
      if (root) root.scrollTop = 0;
    };
    toTop();
    // Re-run after the lazy route chunk paints (Suspense fallback -> page swap).
    const raf = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(raf);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:region/:country" element={<GuideHub />} />
            <Route path="/guides/:slug" element={<GuideSlugRoute />} />
            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" />
      </Router>
    </HelmetProvider>
  );
}

export default App;